import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Lens } from '@/components/datacenter/store'

import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { formatInteger } from '@/lib/inventory'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import { glass } from '@/components/datacenter/hud/primitives'

import {
    Activity,
    ArrowLeft,
    Crosshair,
    Eye,
    HeartPulse,
    ListTree,
    Thermometer,
    Wind
} from 'lucide-react'

const LENSES: { lens: Lens, label: string, icon: LucideIcon }[] = [
    { lens: 'standard', label: 'Live', icon: Eye },
    { lens: 'health', label: 'Health', icon: HeartPulse },
    { lens: 'thermal', label: 'Thermal', icon: Thermometer }
]

interface ToggleProps {
    active: boolean
    label: string
    icon: LucideIcon
    onClick: () => void
    color: string
}

const Toggle: FC<ToggleProps> = ({ active, label, icon: Icon, onClick, color }): ReactNode => {
    return (
        <button
            type='button'
            onClick={onClick}
            aria-pressed={active}
            title={label}
            className={cn(
                'relative flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70',
                active ? 'text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
            )}
            style={active ? { backgroundColor: `${color}1f`, boxShadow: `inset 0 0 0 1px ${color}55` } : undefined}
        >
            <Icon className={cn('size-4', active && 'icon-breathe')} style={active ? { color } : undefined} aria-hidden='true' />
            <span className='hidden lg:inline'>
                {label}
            </span>
        </button>
    )
}

const ControlBar: FC = (): ReactNode => {
    const lens = useDatacenterStore(state => state.lens)
    const setLens = useDatacenterStore(state => state.setLens)
    const airflow = useDatacenterStore(state => state.airflow)
    const traffic = useDatacenterStore(state => state.traffic)
    const toggleAirflow = useDatacenterStore(state => state.toggleAirflow)
    const toggleTraffic = useDatacenterStore(state => state.toggleTraffic)
    const recenter = useDatacenterStore(state => state.recenter)
    const back = useDatacenterStore(state => state.back)
    const canGoBack = useDatacenterStore(state => Boolean(state.selectedRackId || state.selectedDeviceId || state.focus !== 'overview'))
    const setInventoryOpen = useDatacenterStore(state => state.setInventoryOpen)
    const { stats } = useInventory()

    return (
        <motion.div
            className='pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center p-3 sm:p-4'
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className={cn(glass, 'pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto p-1.5 no-scrollbar')}>
                <button
                    type='button'
                    onClick={back}
                    disabled={!canGoBack}
                    className='flex size-10 shrink-0 items-center justify-center rounded-xl text-slate-300 transition hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70 disabled:opacity-30 disabled:hover:bg-transparent'
                    aria-label='Go back one step'
                    title='Back (Esc)'
                >
                    <ArrowLeft className='size-4' aria-hidden='true' />
                </button>

                <span className='mx-1 h-6 w-px shrink-0 bg-white/10' aria-hidden='true' />

                <div role='radiogroup' aria-label='View lens' className='flex shrink-0 items-center gap-0.5'>
                    {LENSES.map(item => {
                        const active = lens === item.lens
                        const Icon = item.icon

                        return (
                            <button
                                key={item.lens}
                                type='button'
                                role='radio'
                                aria-checked={active}
                                onClick={() => setLens(item.lens)}
                                className={cn('relative flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70', active ? 'text-slate-950' : 'text-slate-300 hover:text-white')}
                            >
                                {active && (
                                    <motion.span
                                        layoutId='lens-pill'
                                        className='absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-300 to-sky-300 shadow-[0_0_24px_-4px_rgba(34,211,238,0.8)]'
                                        transition={{ type: 'spring', stiffness: 500, damping: 36 }}
                                    />
                                )}
                                <Icon className='relative size-4' aria-hidden='true' />
                                <span className='relative hidden sm:inline'>
                                    {item.label}
                                </span>
                            </button>
                        )
                    })}
                </div>

                <span className='mx-1 h-6 w-px shrink-0 bg-white/10' aria-hidden='true' />

                <Toggle active={airflow} label='Airflow' icon={Wind} onClick={toggleAirflow} color='#38bdf8' />
                <Toggle active={traffic} label='Traffic' icon={Activity} onClick={toggleTraffic} color='#a78bfa' />

                <span className='mx-1 h-6 w-px shrink-0 bg-white/10' aria-hidden='true' />

                <button
                    type='button'
                    onClick={recenter}
                    className='flex size-10 shrink-0 items-center justify-center rounded-xl text-slate-300 transition hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                    aria-label='Recenter the view'
                    title='Recenter'
                >
                    <Crosshair className='size-4' aria-hidden='true' />
                </button>

                <button
                    type='button'
                    onClick={() => setInventoryOpen(true)}
                    className='ml-1 flex h-10 shrink-0 items-center gap-2 rounded-xl bg-white/10 px-3.5 text-sm font-semibold text-white ring-1 ring-white/10 transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                >
                    <ListTree className='size-4 text-cyan-300' aria-hidden='true' />
                    <span className='hidden sm:inline'>
                        Inventory
                    </span>
                    <span className='rounded-md bg-white/10 px-1.5 py-0.5 font-mono text-[11px] text-slate-300'>
                        {formatInteger(stats.devices)}
                    </span>
                </button>
            </div>
        </motion.div>
    )
}

export default ControlBar
